import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Label, Display } from "./ui";
import { COLLECTIONS, findCollection, productsInCollection } from "./siteData";
import NewArrivals from "./NewArrivals"; // eslint-disable-line no-unused-vars
import FabricStore from "./FabricStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PageHero({ image, label, title, subtitle }) {
  return (
    <section className="relative h-[62vh] w-full overflow-hidden">
      <img src={image} alt={title} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-black/45" />
      <div className="relative mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-16 lg:px-12">
        <Reveal><Label className="text-[#e6d6bd]">{label}</Label>
          <h1 className="mt-4 font-display text-5xl font-light tracking-tight text-white sm:text-6xl">{title}</h1>
          {subtitle && <p className="mt-4 max-w-xl text-white/75">{subtitle}</p>}
        </Reveal>
      </div>
    </section>
  );
}

export default function Collections() {
  const { slug } = useParams();
  const collection = slug ? findCollection(slug) : null;
  const [live, setLive] = useState([]);
  useEffect(() => { axios.get(`${API}/public/products`).then((r) => setLive(r.data || [])).catch(() => setLive([])); }, []);

  if (slug && collection) {
    const liveMatch = live.filter((p) => (p.category || "").toLowerCase() === collection.name.toLowerCase());
    const isLive = liveMatch.length > 0;
    const products = isLive
      ? liveMatch.map((p) => ({ id: p.id, name: p.item_name, material: p.category || "Printed textile", from: "", image: collection.image, live: true }))
      : productsInCollection(collection.name).map((p) => ({ id: p.id, name: p.name, material: p.material, from: p.from, image: p.images[0], live: false }));
    return (
      <div data-testid="collection-detail-page">
        <PageHero image={collection.image} label="Collection" title={collection.name} subtitle={collection.description} />
        <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12">
          <Reveal>
            <Link to="/collections" className="text-sm text-[#595959] underline-offset-4 hover:text-[#B89B72] hover:underline" data-testid="back-to-collections">← All collections</Link>
            {isLive && <span className="ml-4 inline-flex items-center gap-2 rounded-full bg-[#B89B72]/10 px-3 py-1 text-xs tracking-wide text-[#B89B72]" data-testid="live-catalogue-badge"><span className="h-1.5 w-1.5 rounded-full bg-[#B89B72]" />Live from our catalogue</span>}
          </Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Link to={p.live ? "/contact" : `/product/${p.id}`} state={p.live ? { product: p.name } : undefined} className="group block" data-testid={`collection-product-${p.id}`}>
                  <div className="overflow-hidden rounded-md"><img src={p.image} alt={p.name} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                  <h3 className="mt-4 font-display text-xl font-light">{p.name}</h3>
                  <p className="mt-1 text-sm text-[#595959]">{p.material}{p.from ? ` · From ${p.from}` : ""}</p>
                </Link>
              </Reveal>
            ))}
            {products.length === 0 && <p className="text-[#595959]">Pieces coming soon.</p>}
          </div>
        </section>
      </div>
    );
  }

  return <FabricStore />;
}
